import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ModalHeaderComponent } from '@base/components/private/modals/modal-header/modal-header.component';
import { ModalService } from '@base/services/private/modal.service';
import { GlobalStateService } from '@base/services/private/global-state.service';
import { finalize, lastValueFrom, Subscription } from 'rxjs';
import { ModalBodyComponent } from '@base/components/private/modals/modal-body/modal-body.component';
import { LoginLoader } from '@base/models/responses/login-loader.res';
import { ImgErrorFallbackDirective } from '@base/directives/img-error-fallback.directive';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MeetService } from '@base/services/private/meet.service';
import { MessagesVcard } from '@base/models/responses/vcard.res';
import { CompanyMessageVcardParams } from '@base/models/requests/vcard.req';
import { ModalSentSuccessComponent } from '@base/components/private/modals/modal-sent-success/modal-sent-success.component';
import { TranslatePipe } from '@base/pipes/private/translate.pipe';
import { SkeletonGeneralListComponent } from '@base/components/private/skeleton/skeleton-general-list/skeleton-general-list.component';
import { defaultUserImg } from '@base/constants/common';
import { GlobalService } from '@base/services/private/global.service';
import { StatusResponse } from '@base/enums/status-response.enum';
import { ModalFooterComponent } from '@base/components/private/modals/modal-footer/modal-footer.component';

// local enum for active button states
enum ActiveButton {
  SEND_NAMECARD = 'send-namecard',
}

@Component({
  selector: 'app-modal-namecard',
  imports: [
    ModalHeaderComponent,
    ModalBodyComponent,
    ImgErrorFallbackDirective,
    ReactiveFormsModule,
    forwardRef(() => TranslatePipe),
    SkeletonGeneralListComponent,
    ModalFooterComponent,
  ],
  templateUrl: './modal-namecard.component.html',
  styleUrl: './modal-namecard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalNamecardComponent implements OnInit, OnDestroy {
  private readonly modalSrv = inject(ModalService);
  private readonly globalStateSrv = inject(GlobalStateService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly globalSrv = inject(GlobalService);
  private readonly meetSrv = inject(MeetService);

  @Input() modalId = '';
  @Input() exhibitorId = '';
  @Input() isChatRoom = false;

  loader: LoginLoader | null = null;
  form = this.fb.group({
    isHasEmail: [true, []],
    isHasPhone: [true, []],
    countryCode: ['', [Validators.required, Validators.pattern(/^[\d*]+$/)]],
    mobile: ['', [Validators.required, Validators.pattern(/^[\d*]+$/)]],
  });
  vcard: MessagesVcard | null = null;
  chatroomId = '';
  activeButton = '';
  defaultUserImg = defaultUserImg;

  isHttpRequestRunning = false;
  isShowPlaceholder = true;
  isEdit = false;

  httpRequestObserver$: Subscription | null = null;

  ActiveButton = ActiveButton;

  ngOnInit(): void {
    (async () => {
      await this.getGlobalState();
    })();
    this.listenHttpRequestState();
  }

  ngOnDestroy(): void {
    this.globalSrv.destroyModalRef(this.modalId);
  }

  /**
   * Retrieves the global state and updates the loader property.
   * @returns Promise<void>
   */
  async getGlobalState(): Promise<void> {
    try {
      const globalState = await lastValueFrom(this.globalStateSrv.getState$());
      this.loader = globalState.loader;
      this.getMessagesVcard();
      this.onChangesIsHasPhoneControl();
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  }

  /**
   * Listens for HTTP request state changes and updates the component state accordingly.
   * @returns void
   */
  listenHttpRequestState(): void {
    this.httpRequestObserver$ = this.globalSrv.httpRequestObserver().subscribe({
      next: (res) => {
        this.isHttpRequestRunning = res;
        this.triggerChangeDetection();
      },
    });
  }

  /**
   * Subscribes to changes of the phone checkbox and updates validators for country code and mobile fields.
   * @returns void
   */
  onChangesIsHasPhoneControl(): void {
    this.isHasPhoneControl.valueChanges.subscribe({
      next: (data) => {
        if (data) {
          this.countryCodeControl.setValidators([Validators.required, Validators.pattern(/^[\d*]+$/)]);
          this.countryCodeControl.updateValueAndValidity();

          this.mobileControl.setValidators([Validators.required, Validators.pattern(/^[\d*]+$/)]);
          this.mobileControl.updateValueAndValidity();
        } else {
          this.countryCodeControl.setValidators([]);
          this.countryCodeControl.updateValueAndValidity();

          this.mobileControl.setValidators([]);
          this.mobileControl.updateValueAndValidity();
        }
      },
      error: (err) => {
        console.error('Error fetching phone control changes:', err);
      },
    });
  }

  /**
   * Toggles the edit mode state for the form.
   * @returns void
   */
  showFormEdit(): void {
    this.isEdit = !this.isEdit;
  }

  /**
   * Closes the modal with the given modalId.
   * @returns void
   */
  close(): void {
    this.modalSrv.close(this.modalId);
  }

  /**
   * Shows a modal warning if the account is a preview account and status response is -2.
   */
  showModalWarning(): void {
    this.modalSrv.showModalWarningForPreviewAccount();
    return;
  }

  /**
   * Sends the name card information to the exhibitor.
   * Builds the payload from form values and posts it via MeetService.
   * @returns void
   */
  sendNameCard(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;
    this.activeButton = ActiveButton.SEND_NAMECARD;
    const payload: CompanyMessageVcardParams = {
      company_id: this.exhibitorId,
      has_email: this.isHasEmailControl.value ? 1 : 0,
      has_phone: this.isHasPhoneControl.value ? 1 : 0,
      cc: this.isHasPhoneControl.value ? this.countryCodeControl.value : '',
      mobile: this.isHasPhoneControl.value ? this.mobileControl.value : '',
    };

    if (this.isChatRoom) {
      this.globalSrv.setHttpRequestState(true);
      setTimeout(() => {
        this.activeButton = '';
        this.globalSrv.setHttpRequestState(false);
        this.modalSrv.close(this.modalId, {
          key: 'SEND_VCARD',
          value: payload,
        });
      }, 300);
      return;
    }

    this.meetSrv
      .postCompanyMessagesVcard(payload)
      .pipe(
        finalize(() => {
          this.activeButton = '';
        })
      )
      .subscribe({
        next: (res) => {
          this.modalSrv.close(this.modalId);
          if (res.status === StatusResponse.SUCCESS) {
            this.chatroomId = res.chatroom_id;
            this.showMessageSuccessRequest();
          } else if (+res.status === StatusResponse.PREVIEW_ACCOUNT) {
            // Preview account returns status -2, but success returns 'SUCCESS'; always convert to number to check for -2.
            this.showModalWarning();
          } else {
            this.showFailedRequest();
          }
        },
        error: (err) => {
          this.showFailedRequest();
          console.error('Error sending name card:', err);
        },
      });
  }

  get countryCodeControl() {
    return this.form.get('countryCode') as FormControl;
  }

  get mobileControl() {
    return this.form.get('mobile') as FormControl;
  }

  get isHasPhoneControl() {
    return this.form.get('isHasPhone') as FormControl;
  }

  get isHasEmailControl() {
    return this.form.get('isHasEmail') as FormControl;
  }

  /**
   * Retrieves vcard messages from the MeetService and updates the form with the values.
   * @returns void
   */
  getMessagesVcard(): void {
    this.meetSrv.getMessagesVcard().subscribe({
      next: (res) => {
        this.vcard = res;
        this.mobileControl.setValue(this.vcard.mobile);
        this.mobileControl.updateValueAndValidity();
        this.countryCodeControl.setValue(this.vcard.cc);
        this.countryCodeControl.updateValueAndValidity();
        this.isShowPlaceholder = false;

        this.triggerChangeDetection();
      },
      error: (err) => {
        console.error('Error fetching vCard messages:', err);
        this.isShowPlaceholder = false;
      },
    });
  }

  /**
   * Displays a modal indicating that the name card request has failed.
   * @returns void
   */
  showFailedRequest(): void {
    this.modalSrv.showModalFailedRequest();
  }

  /**
   * Displays a success modal after the name card request has been sent successfully.
   * @returns void
   */
  showMessageSuccessRequest(): void {
    const data: Partial<ModalSentSuccessComponent> = {
      chatroomId: this.chatroomId,
    };
    this.modalSrv.showModalSuccessRequest({
      addsData: data,
    });
  }

  /**
   * Triggers change detection for the component.
   */
  triggerChangeDetection(): void {
    this.changeDetectorRef.markForCheck();
    this.changeDetectorRef.detectChanges();
  }
}
